import { useQuery } from 'react-query';
import { AxiosError } from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Axios } from '../../axios-instance';
import { BaseError } from '@utils/types/shared/base';
import { AdminLicenseRequestListResponse } from '@utils/types/back-office/licensing';
import { LicenseRequestStatus } from '@utils/types/resellers/license-request';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params';

export const ADMIN_LICENSE_REQUESTS_QUERY_KEY = 'adminLicenseRequests';

interface GetAdminLicenseRequestsParams {
  status?: LicenseRequestStatus;
}

export const useGetAdminLicenseRequests = (
  params: GetAdminLicenseRequestsParams = {}
) => {
  const { status } = params;
  const { searchParams } = useNewAppSearchParams();
  const [sp] = useSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;
  const sortField = (searchParams.sort as string | undefined) || undefined;
  const sortDirection =
    (searchParams.direction as 'asc' | 'desc' | undefined) || undefined;

  const parsedQ = (() => {
    const result: Record<string, string> = {};
    for (const entry of sp.getAll('q')) {
      const c1 = entry.indexOf(':');
      if (c1 === -1) continue;
      const field = entry.slice(0, c1);
      const rest = entry.slice(c1 + 1);
      const c2 = rest.indexOf(':');
      if (c2 === -1) continue;
      const value = rest
        .slice(c2 + 1)
        .replace(/^%/, '')
        .replace(/%$/, '');
      if (value) result[field] = value;
    }
    return result;
  })();

  const resellerOrgName = parsedQ['reseller_org_name'] || undefined;
  const targetOrgName = parsedQ['target_org_name'] || undefined;
  const orgLimit = parsedQ['org_limit']
    ? Number(parsedQ['org_limit'])
    : undefined;
  const maxProcessing = parsedQ['max_processing']
    ? Number(parsedQ['max_processing'])
    : undefined;
  const requestId = parsedQ['id'] ? Number(parsedQ['id']) : undefined;

  const buildUrl = () => {
    const qp = new URLSearchParams();
    if (status) qp.append('status', status);
    if (offset) qp.append('offset', String(offset));
    qp.append('limit', String(limit));
    if (sortField) qp.append('sort', sortField);
    if (sortDirection) qp.append('direction', sortDirection);
    if (resellerOrgName) qp.append('reseller_org_name', resellerOrgName);
    if (targetOrgName) qp.append('target_org_name', targetOrgName);
    if (orgLimit !== undefined) qp.append('org_limit', String(orgLimit));
    if (maxProcessing !== undefined)
      qp.append('max_processing', String(maxProcessing));
    if (requestId !== undefined) qp.append('id', String(requestId));
    const qs = qp.toString();
    return `api/admin/licensing/requests${qs ? `?${qs}` : ''}`;
  };

  return useQuery<AdminLicenseRequestListResponse, AxiosError<BaseError>>(
    [
      ADMIN_LICENSE_REQUESTS_QUERY_KEY,
      status,
      offset,
      limit,
      sortField,
      sortDirection,
      resellerOrgName,
      targetOrgName,
      orgLimit,
      maxProcessing,
      requestId
    ],
    () =>
      Axios.get<AdminLicenseRequestListResponse>(buildUrl()).then(r => r.data),
    {
      useErrorBoundary: false,
      keepPreviousData: true,
      staleTime: 30 * 1000,
      cacheTime: 5 * 60 * 1000
    }
  );
};
